/**
 * A family's accent, as Tailwind classes the compiler can see.
 *
 * **This exists so that no colour is ever computed into an inline style.** The document side used
 * to build its tinted shadows and gradient discs at render time from a numeric colour, which meant
 * a `style` attribute on every card. It does not need to: there are six accents and twelve
 * families, so the whole space is finite and every treatment can be a static class.
 *
 * The strings below are **complete literals on purpose.** Tailwind finds classes by scanning
 * source text, so `` `bg-${name}` `` produces nothing at all — the class is absent from the
 * stylesheet and the element renders unstyled, with no error anywhere. Every class a component can
 * use has to appear in the source exactly as it will be emitted, and that is what this table is.
 *
 * The canvas is a separate matter: Pixi draws with numbers and cannot use a class. It reads
 * `COLOR` out of `tokens.ts`, which is a lookup of the same CSS this table names.
 */

export const ACCENT_NAMES = [
  "sunny",
  "watermelon",
  "turquoise",
  "grape",
  "mint",
  "sky",
] as const;

export type AccentName = (typeof ACCENT_NAMES)[number];

export interface AccentClasses {
  /** Solid fill, for a primary action. */
  bg: string;
  /** Text in the accent, for a label on a light ground. */
  text: string;
  /** The coloured lift that stops a grid of white cards reading as a table. */
  shadow: string;
  /** The tinted disc a creature sits in. */
  disc: string;
  /** A faint wash, for a card that should carry its accent without shouting. */
  wash: string;
  /** A slightly stronger wash, for a chip or a pill. */
  chip: string;
}

/**
 * Written out per accent rather than assembled, for the reason in the module note: a class built
 * by interpolation is a class Tailwind never sees.
 */
export const ACCENT: Record<AccentName, AccentClasses> = {
  sunny: {
    bg: "bg-sunny",
    text: "text-sunny",
    shadow: "shadow-tint-sunny",
    disc: "disc-sunny",
    wash: "bg-sunny/8",
    chip: "bg-sunny/16",
  },
  watermelon: {
    bg: "bg-watermelon",
    text: "text-watermelon",
    shadow: "shadow-tint-watermelon",
    disc: "disc-watermelon",
    wash: "bg-watermelon/8",
    chip: "bg-watermelon/16",
  },
  turquoise: {
    bg: "bg-turquoise",
    text: "text-turquoise",
    shadow: "shadow-tint-turquoise",
    disc: "disc-turquoise",
    wash: "bg-turquoise/8",
    chip: "bg-turquoise/16",
  },
  grape: {
    bg: "bg-grape",
    text: "text-grape",
    shadow: "shadow-tint-grape",
    disc: "disc-grape",
    wash: "bg-grape/8",
    chip: "bg-grape/16",
  },
  mint: {
    bg: "bg-mint",
    text: "text-mint",
    shadow: "shadow-tint-mint",
    disc: "disc-mint",
    wash: "bg-mint/8",
    chip: "bg-mint/16",
  },
  sky: {
    bg: "bg-sky",
    text: "text-sky",
    shadow: "shadow-tint-sky",
    disc: "disc-sky",
    wash: "bg-sky/8",
    chip: "bg-sky/16",
  },
};

/**
 * Which accent and creature each activity family wears.
 *
 * By FAMILY, not by task. The two A-13 arms of forward span are the same game, and giving them
 * different colours or creatures would tell a child they are different when the whole point of the
 * experiment is that only the wrapper differs.
 */
export type CreatureName =
  | "firefly"
  | "owl"
  | "fish"
  | "cat"
  | "star"
  | "mouse"
  | "robot"
  | "turtle"
  | "bird"
  | "chameleon"
  | "elephant"
  | "penguin"
  | "bee"
  | "frog"
  | "ladybird"
  | "parrot"
  | "squirrel"
  | "bat"
  | "butterfly"
  | "beaver"
  | "ant"
  | "octopus"
  | "snail"
  | "hare";

export interface FamilyLook {
  accent: AccentName;
  creature: CreatureName;
}

const FAMILY: Record<string, FamilyLook> = {
  "wm-span-forward": { accent: "sunny", creature: "firefly" },
  "wm-span-backward": { accent: "grape", creature: "owl" },
  "vs-pattern-match": { accent: "turquoise", creature: "fish" },
  "vs-odd-one-out": { accent: "watermelon", creature: "cat" },
  "vs-build-copy": { accent: "mint", creature: "star" },
  "vs-maze": { accent: "sky", creature: "mouse" },
  "vs-robot": { accent: "turquoise", creature: "robot" },
  "vs-sequence": { accent: "watermelon", creature: "turtle" },
  "vs-difference": { accent: "sky", creature: "bird" },
  "ef-rule-switch": { accent: "mint", creature: "chameleon" },
  "wm-pairs": { accent: "grape", creature: "penguin" },
  "vs-ordering": { accent: "sunny", creature: "elephant" },
  /**
   * The three newest families, which until now had no entry at all.
   *
   * `familyLook` falls back rather than throwing, so a missing family is silent — and all three were
   * wearing the span game's yellow firefly. Three different games with one identity is worse than an
   * ugly icon: a child picking from the shelf could not tell them apart, and neither could a teacher
   * looking at a reward screen.
   */
  "math-arith": { accent: "sunny", creature: "bee" },
  "logic-sudoku": { accent: "sky", creature: "frog" },
  "vs-sequence-color": { accent: "watermelon", creature: "ladybird" },
  /**
   * The alphabet, in grape.
   *
   * Every accent is now worn by three families, which is the whole palette doing its job rather
   * than a shortage: six colours across sixteen games means the colour narrows the shelf and the
   * creature picks the game out of what is left. Grape because the two other games wearing it —
   * backward span and hidden pairs — sit at the other end of the shelf from this one, so no two
   * purple cards are neighbours.
   */
  "lit-letters": { accent: "grape", creature: "parrot" },
  /**
   * Finding a letter in a word, in turquoise.
   *
   * A different accent from the alphabet game on purpose. They are companions and a teacher reads
   * them as a pair, but a CHILD picks from the shelf by colour first and creature second — two
   * purple cards side by side is the one arrangement where the pair being related costs more than
   * it gives.
   */
  "lit-find-letter": { accent: "turquoise", creature: "squirrel" },
  /**
   * Shadow matching, in grape, with a bat.
   *
   * A bat because it is the creature a child associates with the dark, and because it is the one
   * animal on the shelf that is itself mostly a silhouette. Grape for the same reason the alphabet
   * got it: the other purple cards sit at the far end of the shelf, so no two are neighbours.
   */
  "vs-shadow": { accent: "grape", creature: "bat" },
  /**
   * Mirror building, in sky, with a butterfly.
   *
   * The one creature that IS the game: a butterfly is the picture every symmetry lesson for this age
   * band uses, and a child who has folded a painted paper in half has already met it. A different
   * accent from the copying game it shares a renderer with, because on the shelf they must not read
   * as two cards for one activity.
   */
  "vs-mirror": { accent: "sky", creature: "butterfly" },
  /**
   * The part/whole game, in mint, with a beaver.
   *
   * A beaver builds a whole thing out of a heap of separate pieces, which is the game read
   * backwards and the closest an animal gets to it. Mint is shared with the copying game and the
   * sorting one, and all three are about assembling rather than spotting — a colour doing the job
   * the palette note describes, narrowing the shelf before the creature picks the card out of it.
   */
  "vs-parts": { accent: "mint", creature: "beaver" },
  /**
   * Find them all, in sunny, with an ant.
   *
   * An ant is the animal that covers ground rather than the one that spots things, which is the
   * distinction between this game and spot-the-difference next to it on the shelf. Sunny keeps it
   * away from that game's sky blue, because a child who confuses the two cards ends up practising
   * the wrong half of "looking carefully".
   */
  "vs-search": { accent: "sunny", creature: "ant" },
  /**
   * Colour by the key, in grape, with an octopus.
   *
   * An octopus changes colour to order, which is the game said in one picture. Grape because the
   * three games it shares the accent with — backward span, hidden pairs, the alphabet — all sit
   * elsewhere on the shelf, so no two purple cards end up neighbours.
   */
  "vs-code-key": { accent: "grape", creature: "octopus" },
  /**
   * Carrying a motif along a strip, in turquoise, with a snail.
   *
   * A snail carries a spiral — a pattern that repeats as it goes — and it moves along leaving a
   * trail behind it, which is the game's shape exactly. Turquoise rather than the sequence game's
   * watermelon, because the two are neighbours in the pattern strand and a child picking by colour
   * must not land on the wrong one.
   */
  "vs-tiling": { accent: "turquoise", creature: "snail" },
  /**
   * Drive the car, in sunny, with a hare.
   *
   * A hare is the animal that changes direction mid-run, which is the whole game. Sunny rather than
   * the chameleon's mint: the two are the pair this strand is built around — a hidden rule change
   * and a visible one — and a child picking from the shelf must be able to tell them apart before
   * they open either.
   */
  "ef-key-map": { accent: "sunny", creature: "hare" },
};

/** An unknown family falls back rather than throwing: a bundle may ship a game this build predates. */
export const familyLook = (family: string): FamilyLook =>
  FAMILY[family] ?? { accent: "sunny", creature: "firefly" };

/** The class bundle for a family, in one call. */
export const familyClasses = (family: string): AccentClasses => ACCENT[familyLook(family).accent];
